// src/hooks/useProductRatings.js
// Shared, cached lookup from product title -> { average, count }, built from
// a single site-wide reviews fetch — so mounting many ProductRatingBadge
// cards across a grid triggers one shared network request, not one per card.

import { useEffect, useState } from 'react';
import { getReviews } from '../lib/reviewsApi';

// Matches the site-wide cap enforced by netlify/functions/reviews.js when no
// productId is passed (SITE_WIDE_MAX_LIMIT there) — requesting more than this
// wouldn't get us more data anyway.
const SITE_WIDE_SAMPLE_LIMIT = 20;
const CACHE_TTL_MS = 5 * 60 * 1000;

let cache = null; // { promise, fetchedAt }

function normalizeTitle(title) {
    return typeof title === 'string' ? title.trim().toLowerCase() : '';
}

// netlify/functions/reviews.js's mapped review shape only exposes
// `product_title` to the client (no product id survives the Judge.me proxy),
// so title is the only field available here to group reviews by product.
function aggregateByTitle(reviews) {
    const totals = {};
    for (const review of Array.isArray(reviews) ? reviews : []) {
        const key = normalizeTitle(review && review.product_title);
        const rating = review && review.rating;
        if (!key || typeof rating !== 'number' || !Number.isFinite(rating)) continue;
        if (!totals[key]) totals[key] = { sum: 0, count: 0 };
        totals[key].sum += rating;
        totals[key].count += 1;
    }

    const result = {};
    Object.keys(totals).forEach((key) => {
        result[key] = { average: totals[key].sum / totals[key].count, count: totals[key].count };
    });
    return result;
}

function getCachedRatings() {
    if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
        return cache.promise;
    }
    const promise = getReviews({ limit: SITE_WIDE_SAMPLE_LIMIT })
        .then(aggregateByTitle)
        .catch(() => ({}));
    cache = { promise, fetchedAt: Date.now() };
    return promise;
}

// Returns getRating(productTitle) -> { average, count } | null.
export function useProductRatings() {
    const [ratingsByTitle, setRatingsByTitle] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getCachedRatings().then((ratings) => {
            if (!cancelled) setRatingsByTitle(ratings || {});
        });
        return () => {
            cancelled = true;
        };
    }, []);

    function getRating(productTitle) {
        if (!ratingsByTitle) return null;
        const key = normalizeTitle(productTitle);
        return (key && ratingsByTitle[key]) || null;
    }

    return { getRating };
}
